import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIVE_LANGS } from '@/lib/i18n';
import { Globe, ChevronDown, Check } from 'lucide-react';

/**
 * Language selector — compact dropdown.
 * `variant='link'` renders a lightweight text button (used in landing nav).
 * `variant='pill'` renders a bordered pill (used inside app top bar).
 */
export function LanguageSwitcher({ variant = 'link', placement = 'down' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const active = ACTIVE_LANGS.find((l) => l.code === i18n.language) || ACTIVE_LANGS[0];

  const btnCls = variant === 'pill'
    ? 'ghost-btn h-9 px-3 text-xs'
    : variant === 'field'
      ? 'inline-flex items-center gap-2 h-10 px-3 rounded-xl bg-white border border-graphite-border text-sm font-medium text-graphite-700 hover:text-graphite-900 hover:border-graphite-400 transition-colors duration-200'
      : 'inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg text-sm font-medium text-graphite-700 hover:text-graphite-900 hover:bg-paper-200 transition-colors';

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="language-switcher"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={btnCls}
      >
        <Globe size={14} />
        <span>{active.native}</span>
        <ChevronDown size={14} className={`transition-transform duration-150 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute w-44 bg-white border border-graphite-border rounded-xl shadow-soft-lg z-50 overflow-hidden ${
          placement === 'up' ? 'bottom-full mb-2 left-0' : 'right-0 mt-2'}`}>
          {ACTIVE_LANGS.map((l) => {
            const isActive = l.code === i18n.language;
            return (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-paper-200 transition-colors ${isActive ? 'text-graphite-900 font-semibold' : 'text-graphite-700'}`}
              >
                <span>{l.native}</span>
                {isActive && <Check size={14} className="text-amber-500" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
